import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Tooltip from '@mui/material/Tooltip';
import { LoadingButton } from '@mui/lab';
import { Button, Chip, MenuItem, Stack, TableCell, TableRow, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useNavigate } from 'react-router';
import { components } from 'src/types/schema';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';
import { hasPermissions } from 'src/auth/guard/utils';
import { usePopover } from 'src/components/custom-popover';
import CustomPopover from 'src/components/custom-popover/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  getComparator,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { RestaurantFoods, updateRestaurantFood, useGetRestaurantFoods } from 'src/api/restaurant';
import { IRestaurantTableFilters, IRestaurantTableFilterValue } from 'src/types/user';
import RestaurantTableToolbar from '../restaurant-table-toolbar';
import { FoodImportModal, FoodExportModal } from '../components';

// ----------------------------------------------------------------------

const defaultFilters: IRestaurantTableFilters = {
  title: '',
  is_available: false,
};

// ----------------------------------------------------------------------

  const tableConfig = {
    id: 'food',
    header: [
      { id: 'name_en', label: 'Food Name', alignRight: false },
      { id: 'food_meal_tags', label: 'Meal Types', width: 180 },
      { id: 'availability_patterns', label: 'Repeat', width: 180 },
      { id: 'availability_dates', label: 'Days', width: 180 },
      { id: 'status', label: 'Status', width: 120 },
      { id: 'action', label: '', width: 80, alignRight: true },
    ],
    data: [],
  };

  export default function RestaurantFoodsListView({ id }: { id: string }) {
    const table = useTable({
      defaultRowsPerPage: 25,
      defaultOrder: 'asc',
    });

    const [filters, setFilters] = useState(defaultFilters);

    const [pagination, setPagination] = useState({
      page: table.page,
      rowsPerPage: table.rowsPerPage,
    });

    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);

    const { restaurantFoods, restaurantFoodsLoading } = useGetRestaurantFoods(
      id,
      pagination.page + 1,
      pagination.rowsPerPage,
      filters.title
    );

    const confirm = useBoolean();
    const importModal = useBoolean();
    const exportModal = useBoolean();

    const [tableData, setTableData] = useState(restaurantFoods?.results || []);

    const canUpdateFood = hasPermissions(user, ['partners:update:foods']);

    const dataFiltered = applyFilter({
      inputData: tableData,
      comparator: getComparator(table.order, table.orderBy),
      filters,
    });

    const dataInPage = dataFiltered.slice(0, table.rowsPerPage);

    const denseHeight = table.dense ? 52 : 72;

    const canReset = true;

    const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

    const handleDeleteRow = useCallback(
      async (uid: number) => {
        await updateRestaurantFood({ id: uid, is_deleted: true });
        const deleteRow = tableData.filter((row) => row.id !== uid);
        setTableData(deleteRow);

        table.onUpdatePageDeleteRow(dataInPage.length);
      },
      [dataInPage.length, table, tableData]
    );

    const handleDeleteRows = useCallback(async () => {
      setIsDeleteProcessing(true);
      const deleteRows = tableData.filter(async (row) => {
        if (table.selected.includes(`${row.id}`)) {
          await updateRestaurantFood({ id: row.id, is_deleted: true });
        }
        return !table.selected.includes(`${row.id}`);
      });
      setTableData(deleteRows);

      table.onUpdatePageDeleteRows({
        totalRows: tableData.length,
        totalRowsInPage: dataInPage.length,
        totalRowsFiltered: dataFiltered.length,
      });

      setIsDeleteProcessing(false);
    }, [dataFiltered.length, dataInPage.length, table, tableData]);

    const handleEditRow = useCallback(
      (uid: number) => {
        if (canUpdateFood) {
          navigate(paths.dashboard.restaurant.food.edit(uid.toString()));
        }
      },
      [canUpdateFood, navigate]
    );

    const handleFilters = useCallback(
      (name: string, value: IRestaurantTableFilterValue) => {
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);

        const newPagination = { ...pagination, page: 0 }; // Assuming your page indexing starts at 0
        setPagination(newPagination);
        table.setPage(0);

        localStorage.setItem('restaurantFoodFilters', JSON.stringify(newFilters));
        // Reset pagination in localStorage when filters change
        localStorage.setItem('restaurantFoodPagination', JSON.stringify(newPagination));
      },
      [filters, pagination, table]
    );

    const handlePageChange = useCallback(
      (event: any, newPage: number) => {
        table.setPage(newPage);
        const newPagination = { ...pagination, page: newPage };
        setPagination(newPagination);
        localStorage.setItem('restaurantFoodPagination', JSON.stringify(newPagination));
      },
      [pagination, table]
    );

    const handleRowsPerPageChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        table.setRowsPerPage(newRowsPerPage);
        const newPagination = { ...pagination, page: 0, rowsPerPage: newRowsPerPage };
        setPagination(newPagination);
        localStorage.setItem('restaurantFoodPagination', JSON.stringify(newPagination));
      },
      [pagination, table]
    );

    useEffect(() => {
      const savedFilters = JSON.parse(localStorage.getItem('restaurantFoodFilters') || '{}');
      const savedPagination = JSON.parse(localStorage.getItem('restaurantFoodPagination') || '{}');

      if (Object.keys(savedFilters).length > 0) {
        setFilters(savedFilters);
      }

      if (savedPagination.page !== undefined && savedPagination.rowsPerPage !== undefined) {
        setPagination(savedPagination);
        table.setPage(savedPagination.page);
        table.setRowsPerPage(savedPagination.rowsPerPage);
      }
    }, [table]);

    useEffect(() => {
      if (restaurantFoods?.results && !restaurantFoodsLoading) {
        setTableData(restaurantFoods?.results);
      }
    }, [restaurantFoods, restaurantFoodsLoading]);

    return (
      <>
        <Card>
            <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
              <RestaurantTableToolbar filters={filters} onFilters={handleFilters} />

              <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'flex-end', pr: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="mdi:file-export-outline" />}
                  onClick={exportModal.onTrue}
                >
                  Export
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="mdi:file-import-outline" />}
                  onClick={importModal.onTrue}
                >
                  Import
                </Button>
              </Stack>

              <TableSelectedAction
                dense={table.dense}
                numSelected={table.selected.length}
                rowCount={tableData.length}
                onSelectAllRows={(checked) =>
                  table.onSelectAllRows(
                    checked,
                    tableData.map((row) => row.id.toString())
                  )
                }
                action={
                  <Tooltip title="Delete">
                    <IconButton color="primary" onClick={confirm.onTrue}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  </Tooltip>
                }
              />

              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 500 }}>
                  <TableHeadCustom
                    order={table.order}
                    orderBy={table.orderBy}
                    headLabel={tableConfig.header}
                    rowCount={tableData.length}
                    numSelected={table.selected.length}
                    onSort={table.onSort}
                  />

                  <TableBody>
                    {restaurantFoodsLoading
                      ? [...Array(table.rowsPerPage)].map((i, index) => (
                          <TableSkeleton key={index} sx={{ height: denseHeight }} />
                        ))
                      : dataFiltered
                          .slice(0, table.rowsPerPage)
                          .map((row) => (
                            <FoodTableRow
                              key={row.id}
                              row={row}
                              selected={table.selected.includes(row.id.toString())}
                              onDeleteRow={() => handleDeleteRow(row.id)}
                              onEditRow={() => handleEditRow(row.id)}
                            />
                          ))}

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={restaurantFoods?.count || 0}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </Card>

        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Delete"
          content={
            <>
              Are you sure want to delete <strong> {table.selected.length} </strong> items?
            </>
          }
          action={
            <LoadingButton
              loading={isDeleteProcessing}
              startIcon={<Iconify icon="mdi:trash-can-outline" />}
              variant="contained"
              color="error"
              onClick={async () => {
                await handleDeleteRows();
                confirm.onFalse();
              }}
            >
              Delete
            </LoadingButton>
          }
        />

        {/* Import Modal */}
        <FoodImportModal
          open={importModal.value}
          onClose={importModal.onFalse}
          restaurantId={id}
          onSuccess={() => {
            // Refresh the food list after successful import
            window.location.reload();
          }}
        />

        {/* Export Modal */}
        <FoodExportModal
          open={exportModal.value}
          onClose={exportModal.onFalse}
          restaurantId={id}
        />
      </>
    );
  }

  // ----------------------------------------------------------------------

  function applyFilter({
    inputData,
    comparator,
    filters,
  }: {
    inputData: RestaurantFoods;
    comparator: (a: any, b: any) => number;
    filters: IRestaurantTableFilters;
  }) {
    const stabilizedThis = inputData?.map((el, index) => [el, index] as const);

    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });

    inputData = stabilizedThis.map((el) => el[0]);

    return inputData;
  }

  type FoodTableRowProps = {
    row: components['schemas']['PartnersFood'];
    selected: boolean;
    onDeleteRow: VoidFunction;
    onEditRow: VoidFunction;
  };

  function FoodTableRow({ row, selected, onDeleteRow, onEditRow }: FoodTableRowProps) {
    const { user } = useAuthContext();
    const confirm = useBoolean();
    const popover = usePopover();

    const formatAvailabilityPattern = (patterns: typeof row.availability_patterns): {repeat: string, days: string} => {
      if (!patterns.length) return {'repeat': '-', 'days': '-'};
      if ((new Set(patterns.map((pattern) => pattern.interval))).size > 1) return {'repeat': 'Multiple', 'days': '-'};

      const pattern = patterns[0];
      const interval =
        pattern.interval && pattern.interval > 1 ? `${pattern.interval} weeks` : 'Weekly';
      return {'repeat': interval, 'days': patterns.map((p) => p.day.name_en).join(', ')};
    };

    return (
      <TableRow hover selected={selected}>
        <TableCell>
          <Typography
            onClick={onEditRow}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            variant="subtitle2"
            noWrap
          >
            {row.name_en}
          </Typography>
        </TableCell>

        <TableCell>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {row.food_meal_tags.map((tag) => (
              <Chip key={tag.id} label={tag.meal_tag.name_en} size="small" variant="outlined" />
            ))}
          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" noWrap>
            {row.availability_dates.length ? '-' : formatAvailabilityPattern(row.availability_patterns).repeat}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" noWrap>
            {row.availability_dates.length ? 'Custom' : formatAvailabilityPattern(row.availability_patterns).days}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip
            label={row.status || 'active'}
            color={row.status === 'active' ? 'success' : 'default'}
            size="small"
          />
        </TableCell>

        {hasPermissions(user, ['partners:update:foods']) && (
          <TableCell align="left" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>

            <CustomPopover
              open={popover.open}
              onClose={popover.onClose}
              arrow="right-top"
              sx={{ width: 140 }}
            >
              <MenuItem
                onClick={() => {
                  onEditRow();
                  popover.onClose();
                }}
                sx={{ pl: 2, color: '#212B36' }}
              >
                <Iconify icon="solar:pen-bold" />
                <span style={{ color: '#212B36' }}>Edit</span>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  confirm.onTrue();
                  popover.onClose();
                }}
                sx={{ color: 'error.main', pl: 2 }}
              >
                <Iconify icon="solar:trash-bin-trash-bold" />
                Delete
              </MenuItem>
            </CustomPopover>

            <ConfirmDialog
              open={confirm.value}
              onClose={confirm.onFalse}
              title="Delete"
              content="Are you sure want to delete?"
              action={
                <Button variant="contained" color="error" onClick={onDeleteRow}>
                  Delete
                </Button>
              }
            />
          </TableCell>
        )}
      </TableRow>
    );
  }
